import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, TrendingUp, Shield, Clock, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const HomePage = () => {
  const features = [
    {
      icon: <CheckCircle2 className="h-8 w-8 text-blue-600" />,
      title: 'Accurate & Compliant',
      description: 'Expert tax preparation following all CRA guidelines and regulations',
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-blue-600" />,
      title: 'Business Growth',
      description: 'Strategic planning and consulting to help your business thrive',
    },
    {
      icon: <Shield className="h-8 w-8 text-blue-600" />,
      title: 'Trusted Service',
      description: 'Confidential handling of your financial information with integrity',
    },
    {
      icon: <Clock className="h-8 w-8 text-blue-600" />,
      title: 'Quick Response',
      description: 'Fast turnaround times and responsive customer support',
    },
  ];

  const services = [
    'Personal Tax Returns (T1)',
    'Newcomer Tax Services',
    'Student Tax Returns',
    'Self-Employed Tax Filing',
    'International Income Reporting',
    'CRA Assistance & Support',
  ];

  const testimonials = [
    {
      name: 'Priya Sharma',
      business: 'New Immigrant',
      rating: 5,
      text: 'As a newcomer to Canada, filing taxes was overwhelming. ManoMay made it easy and helped me get a great refund!',
    },
    {
      name: 'David Kim',
      business: 'Self-Employed',
      rating: 5,
      text: 'Professional, reliable, and always available when I need help. They maximized my deductions and saved me money.',
    },
    {
      name: 'Jessica Chen',
      business: 'Graduate Student',
      rating: 5,
      text: 'They helped me claim all my tuition credits and explained everything clearly. Got my refund in just 2 weeks!',
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  Get What Is Yours
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Professional tax preparation services for students, working professionals, self-employed individuals, and newcomers to Canada. 
                  Maximize your refunds with expert guidance.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6">
                  <Link to="/contact">
                    Book a Consultation <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50 text-lg px-8 py-6">
                  <Link to="/services">
                    View Services
                  </Link>
                </Button>
              </div>
              <div className="flex items-center space-x-6 pt-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-10 w-10 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-sm">{i}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Trusted by 100+ clients</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&q=80"
                alt="Professional business meeting"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <p className="text-sm font-medium text-gray-600">Taxes filed within</p>
                <p className="text-3xl font-bold text-blue-600">24-48hrs</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Manomay Tax?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional tax services for individuals, students, working professionals, and newcomers to Canada
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-2 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&q=80"
                alt="Accounting services"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Comprehensive Tax Services
              </h2>
              <p className="text-lg text-gray-600">
                From first-time filers to experienced taxpayers, we provide professional assistance for students, 
                working professionals, self-employed individuals, and newcomers navigating Canadian tax requirements.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{service}</span>
                  </div>
                ))}
              </div>
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white mt-4">
                <Link to="/services">
                  Explore All Services <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Don't just take our word for it - see what our clients have to say
            </p>
            <Button asChild variant="outline" size="lg" className="border-blue-600 text-blue-600 hover:bg-blue-50">
              <a href="https://www.google.ca/search?ibp=gwp;0,7&q=ManoMay+Tax+Inc.&ludocid=8456212877382274207&lsig=AB86z5UoK5CpReZezL1rODCCaMkh#lkt=LocalPoiReviews" target="_blank" rel="noopener noreferrer">
                View All Google Reviews
              </a>
            </Button>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-2 hover:shadow-lg transition-shadow">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.business}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Book a free consultation today and let's discuss how we can help maximize your tax refund
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6">
              <Link to="/contact">
                Book Free Consultation
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-blue-700 text-lg px-8 py-6">
              <a href="https://wa.me/message/E7OKL6FXEAG6L1" target="_blank" rel="noopener noreferrer">
                Chat on WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Professional Fees Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Transparent & Competitive Pricing
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional tax services with clear, upfront pricing. No hidden fees, no surprises.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Individual Services */}
            <Card className="border-2 hover:border-blue-300 transition-all duration-300">
              <CardHeader className="bg-blue-50">
                <CardTitle className="text-xl">Individual Tax Returns</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex justify-between">
                    <span>Basic T1 Return</span>
                    <span className="font-semibold">Starting at $40</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Student Return</span>
                    <span className="font-semibold">Starting at $40</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Newcomer Filing</span>
                    <span className="font-semibold">Starting at $40</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Self-Employed</span>
                    <span className="font-semibold">Starting at $100</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">*Prices vary based on complexity</p>
              </CardContent>
            </Card>

            {/* Business Services */}
            <Card className="border-2 hover:border-blue-300 transition-all duration-300">
              <CardHeader className="bg-blue-50">
                <CardTitle className="text-xl">Business Services</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex justify-between">
                    <span>Corporation Tax (T2)</span>
                    <span className="font-semibold">Starting at $125</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Monthly Bookkeeping</span>
                    <span className="font-semibold">Starting at $50/mo</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Payroll Services</span>
                    <span className="font-semibold">Starting at $100/mo</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Business Registration</span>
                    <span className="font-semibold">Starting at $150</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">*Custom packages available</p>
              </CardContent>
            </Card>

            {/* Additional Services */}
            <Card className="border-2 hover:border-blue-300 transition-all duration-300">
              <CardHeader className="bg-blue-50">
                <CardTitle className="text-xl">Additional Services</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex justify-between">
                    <span>CRA Notice Response</span>
                    <span className="font-semibold">Starting at $50</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Tax Planning Consultation</span>
                    <span className="font-semibold">$50/hour</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Prior Year Filing</span>
                    <span className="font-semibold">Starting at $40</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Audit Support</span>
                    <span className="font-semibold">Custom Quote</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">*Contact for detailed quote</p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 bg-blue-50 p-8 rounded-2xl max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Get a Personalized Quote</h3>
            <p className="text-gray-700 mb-6">
              Every tax situation is unique. Contact us for a free consultation and receive a customized quote tailored to your specific needs.
            </p>
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
              <Link to="/contact">Request Free Quote</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Leave a Review CTA */}
      <section className="py-16 bg-slate-50">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Love Our Service?</h3>
          <p className="text-gray-600 mb-6">Share your experience and help other businesses find us</p>
          <Button asChild variant="outline" size="lg" className="border-blue-600 text-blue-600 hover:bg-blue-50">
            <a href="https://g.page/r/CZ9MdB3PgFp1EAI/review" target="_blank" rel="noopener noreferrer">
              Leave a Google Review
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
